// TS types mirroring docs/CONTRACTS.md §3 (data model) and §7 (HTTP API).
// Keep these in lock-step with the contract — do not invent fields/routes.

/** Per-field provenance. CONTRACTS §3 / §4. `caption` is DEFINITIVE; `manual` = a human edit. */
export type FieldSource =
  | "caption"
  | "nga"
  | "met"
  | "wikidata"
  | "gemini"
  | "manual";

export type ArtifactStatus = "pending" | "needs_review" | "confirmed";

export type ImageFidelity = "online_match" | "original_placeholder";

export type ImageSource = "nga" | "met" | "wikidata" | "reverse" | "original";

/**
 * Artifact class. "artwork" (default) runs the identity/enrichment happy path;
 * "general" is a non-artwork / non-discoverable item (a map, a fragment) that
 * skips online lookup but still gets tour tags + general tags + freeform notes
 * (continuation 2026-06-07 P2 "general artifacts"). Absent ⇒ treat as "artwork".
 */
export type ArtifactKind = "artwork" | "general";

/** One DynamoDB item per artifact. CONTRACTS §3. All descriptive fields optional. */
export interface Artifact {
  // system-managed (always set)
  PK1: string; // "ARTIFACT#<ULID>"
  id: string; // bare ULID
  entityType: "ARTIFACT";
  status: ArtifactStatus;
  createdAt: string; // ISO8601
  updatedAt: string; // ISO8601
  originalImageKey: string; // "<ulid>/original.jpg"
  imageFidelity: ImageFidelity;
  searchBlob: string; // lowercased concat of text fields

  // classification: "artwork" (default, runs enrichment) | "general" (no lookup).
  // Absent ⇒ treat as "artwork" (back-compat for records minted before this field).
  kind?: ArtifactKind;

  // descriptive (all optional)
  title?: string;
  artist?: string;
  dateDisplay?: string;
  yearStart?: number; // negative = BCE
  yearEnd?: number;
  styleTags?: string[];
  generalTags?: string[];
  tourTags?: string[];
  country?: string;
  medium?: string;
  misc?: string;
  dimensions?: string;
  captionText?: string; // verbatim OCR of the printed caption/label
  notes?: string; // freeform notes (mainly for kind="general" items)

  // images
  canonicalImageKey?: string; // "<ulid>/canonical.<ext>"
  thumbnailKey?: string; // "<ulid>/thumb.jpg"
  imageSource?: ImageSource;
  sourceImageUrl?: string;
  iiifUrl?: string;
  /**
   * Up to 5 reverse-image candidate URLs persisted by enrich in EVERY case (not
   * just imageSource==="reverse"), so the reviewer always has an escape hatch:
   * even a structured NGA/Met match can be a reproduction (a lithograph) rather
   * than the work itself. The matched source stays the default canonical; these
   * are alternatives the reviewer can commit via use-reverse-candidate
   * (continuation 2026-06-07j; orig. 2026-06-07 P2 "top-5 reverse-image picker").
   */
  reverseImageCandidates?: string[];

  // provenance / search
  ngaObjectId?: string;
  fieldSources?: Record<string, FieldSource>; // per descriptive field
  confidence?: number;
  enrichmentError?: string; // set if enrich failed
}

/**
 * Descriptive fields that are individually editable via `PUT /artifacts/{id}`.
 * CONTRACTS §7: title, artist, dateDisplay, yearStart, yearEnd, styleTags,
 * generalTags, tourTags, country, medium, misc, dimensions, captionText.
 * The server sets fieldSources=manual for any of these it receives and
 * recomputes searchBlob/updatedAt.
 */
export interface EditableFields {
  title?: string;
  artist?: string;
  dateDisplay?: string;
  yearStart?: number;
  yearEnd?: number;
  styleTags?: string[];
  generalTags?: string[];
  tourTags?: string[];
  country?: string;
  medium?: string;
  misc?: string;
  dimensions?: string;
  captionText?: string;
  notes?: string;
}

/** The exact set of editable scalar/array keys, for iterating in the UI. */
export const EDITABLE_TEXT_FIELDS = [
  "title",
  "artist",
  "dateDisplay",
  "country",
  "medium",
  "dimensions",
  "misc",
  "captionText",
  "notes",
] as const satisfies readonly (keyof EditableFields)[];

export const EDITABLE_NUMBER_FIELDS = [
  "yearStart",
  "yearEnd",
] as const satisfies readonly (keyof EditableFields)[];

export const EDITABLE_TAG_FIELDS = [
  "styleTags",
  "generalTags",
  "tourTags",
] as const satisfies readonly (keyof EditableFields)[];

export type EditableTextField = (typeof EDITABLE_TEXT_FIELDS)[number];
export type EditableNumberField = (typeof EDITABLE_NUMBER_FIELDS)[number];
export type EditableTagField = (typeof EDITABLE_TAG_FIELDS)[number];

// ---------------------------------------------------------------------------
// HTTP API request/response shapes (CONTRACTS §7)
// ---------------------------------------------------------------------------

/** `POST /uploads` body. contentType is optional; server defaults to image/jpeg. */
export interface CreateUploadRequest {
  contentType?: "image/jpeg";
  /**
   * Artifact class (continuation 2026-06-07 P2). "general" skips online lookup,
   * so the server mints it straight to status="needs_review" (no enrich call).
   * Absent ⇒ "artwork" (the enrichment happy path).
   */
  kind?: ArtifactKind;
}

/** `POST /uploads` → 200. */
export interface CreateUploadResponse {
  id: string; // bare ULID
  key: string; // "<ulid>/original.jpg"
  uploadUrl: string; // presigned S3 PUT URL
}

/** `GET /artifacts?status=` → 200. */
export interface ListArtifactsResponse {
  items: Artifact[];
}

/** Query value for `GET /artifacts?status=`. */
export type ListStatusQuery = ArtifactStatus | "all";

/**
 * `PUT /artifacts/{id}` body: a partial of the editable fields plus optional
 * status (e.g. to confirm). Edits set fieldSources=manual server-side; on
 * confirm OR edit-of-confirmed the server regenerates catalog.json.
 */
export type UpdateArtifactRequest = EditableFields & {
  status?: ArtifactStatus;
  /** Reclassify artwork ⇄ general (server validates the enum; not provenance-tracked). */
  kind?: ArtifactKind;
};

/** Shape of a 4xx/5xx error body. CONTRACTS §7. */
export interface ApiErrorBody {
  error: string;
}
